import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";
import { Plus, Target, ClipboardCheck, ChevronRight, Trash2 } from "lucide-react";
import GoalFormDialog from "@/components/career/GoalFormDialog";
import PathBackdrop from "@/components/career/PathBackdrop";
import WhatNextPaywall from "@/components/WhatNextPaywall";
import { GET_SEEN_TEMPLATE } from "@/constants/getSeenTemplate";
import ActorProfile from "@/components/career/ActorProfile";

const SUGGESTIONS = {
  pl: [
    "Zbudować bazę polskich reżyserów castingu",
    "Rozwinąć relacje z reżyserami castingu",
    "Znaleźć agenta w Polsce",
    "Zwiększyć liczbę selftape'ów",
    "Rozwinąć kontakty branżowe",
    "Przygotować się do wejścia na rynek zagraniczny",
  ],
  en: [
    "Find representation in Los Angeles",
    "Build a casting director network",
    "Increase TV and film auditions",
    "Build industry relationships",
    "Prepare for the US/UK market",
  ],
};

/* Backdrop moved to shared component: /app/frontend/src/components/career/PathBackdrop.jsx */

export default function CareerPath() {
  const { lang, access, loading: authLoading } = useAuth();
  // Access still loading → never route as unentitled; tiles stay disabled.
  const accessLoading = authLoading || !access;
  const activeGoalsRef = useRef(null);
  const t = (k) => tr(k, lang);
  const nav = useNavigate();

  const [view, setView] = useState("root");    // root | plan | actor_profile
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const v = searchParams.get("view");
    if (v === "actor_profile") setView("actor_profile");
    else if (v === "plan") setView("plan");
    // Any other / missing value → leave current view (default "root").
  }, [searchParams]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [prefillTitle, setPrefillTitle] = useState("");
  const [formTemplate, setFormTemplate] = useState(null);
  const [careerPaywallOpen, setCareerPaywallOpen] = useState(false);

  // Preflight against the existing Career Path plan limit. Matches the
  // backend `check_career_path_quota` rule — the frontend just short-circuits
  // opening the workbook when the plan slot is already exhausted so the user
  // sees the contextual paywall (LEO START / LEO MONTHLY) instead of the
  // workbook they can't submit anyway. Backend enforcement stays as the
  // authoritative gate on POST /api/career/goals.
  const careerAvailable = (
    (access?.limits?.career_paths ?? 0) > (access?.usage?.career_paths_used_period ?? 0)
  );

  const load = async () => {
    try {
      const r = await api.get("/career/goals");
      setGoals(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNewFromSuggestion = (s) => {
    // FREE users are NEVER shown the modal paywall on Career Paths. If they
    // have no career-path slot left they're sent to /pricing; if they still
    // have their FREE slot they open the form like anyone else.
    if (access?.plan === "free") {
      if (!careerAvailable) { nav("/pricing"); return; }
    } else if (!careerAvailable) { setCareerPaywallOpen(true); return; }
    setPrefillTitle(s);
    setFormTemplate(null);
    setFormOpen(true);
  };

  const openBlank = () => {
    if (access?.plan === "free") {
      if (!careerAvailable) { nav("/pricing"); return; }
    } else if (!careerAvailable) { setCareerPaywallOpen(true); return; }
    setPrefillTitle("");
    setFormTemplate(null);
    setFormOpen(true);
  };

  const openUkAgentTemplate = () => {
    if (accessLoading) return;
    // FREE users always land on the preview page — never the modal paywall,
    // regardless of quota state or race conditions on `access`.
    if (access?.plan === "free") { nav("/career/preview/uk_agent"); return; }
    if (!careerAvailable) { setCareerPaywallOpen(true); return; }
    setPrefillTitle(lang === "en" ? "Find an Agent in the UK" : "Znaleźć agenta w UK");
    setFormTemplate("uk_agent");
    setFormOpen(true);
  };

  const openGetMoreAuditionsTemplate = () => {
    if (accessLoading) return;
    if (access?.plan === "free") { nav("/career/preview/get_more_auditions"); return; }
    if (!careerAvailable) { setCareerPaywallOpen(true); return; }
    setPrefillTitle(lang === "en" ? "Get More Auditions in the UK" : "Zwiększ liczbę castingów w UK");
    setFormTemplate("get_more_auditions");
    setFormOpen(true);
  };

  const openGetSeenTemplate = () => {
    if (accessLoading) return;
    if (access?.plan === "free") { nav("/pricing"); return; }
    if (!careerAvailable) { setCareerPaywallOpen(true); return; }
    setPrefillTitle(GET_SEEN_TEMPLATE[lang === "pl" ? "pl" : "en"].name);
    setFormTemplate("get_seen");
    setFormOpen(true);
  };

  const openMoreCastingsPlTemplate = () => {
    if (accessLoading) return;
    if (access?.plan === "free") { nav("/career/preview/more_castings_pl"); return; }
    if (!careerAvailable) { setCareerPaywallOpen(true); return; }
    setPrefillTitle("Zwiększ liczbę castingów w Polsce");
    setFormTemplate("more_castings_pl");
    setFormOpen(true);
  };

  const onGoalCreated = (g) => {
    setGoals((prev) => [g, ...prev]);
    // Land the user on the new goal's detail so they can pick self / Leo plan
    nav(`/career/goals/${g.goal_id}`);
  };

  const delGoal = async (g) => {
    if (!window.confirm(t("goal.confirm_delete"))) return;
    try {
      await api.delete(`/career/goals/${g.goal_id}`);
      setGoals((prev) => prev.filter((x) => x.goal_id !== g.goal_id));
      toast.success(t("goal.deleted"));
    } catch { toast.error(t("goal.save_fail")); }
  };

  return (
    <div className="relative" data-testid="career-path-page">
      <PathBackdrop />

      <div>
        <div className="mono text-[11px] tracking-[0.4em] text-amber-500 uppercase flex items-center gap-2">
          <Target size={12} /> {t("path.title")}
        </div>
        <h1 className="serif text-5xl text-stone-100 mt-2">{t("path.subtitle")}</h1>
        <button
          type="button"
          data-testid="career-active-paths-btn"
          onClick={() => {
            if (view !== "plan") setView("plan");
            setTimeout(() => activeGoalsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
          }}
          className="ghost-btn inline-flex mt-4"
        >
          {lang === "pl" ? "Moje rozpoczęte ścieżki kariery" : "My active career paths"}
        </button>
      </div>

      {view === "root" ? (
        <div className="grid grid-cols-1 gap-4 mt-8 max-w-2xl">
          <motion.button
            data-testid="path-tile-plan"
            onClick={() => setView("plan")}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="paper p-6 text-left hover:border-amber-500/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Target className="text-amber-500" size={18} />
              <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
                {t("path.pick_plan")}
              </div>
            </div>
            <p className="text-stone-400 text-sm mt-3">{t("path.pick_plan_body")}</p>
          </motion.button>

          <motion.button
            data-testid="path-tile-actor-profile"
            onClick={() => setView("actor_profile")}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="paper p-6 text-left hover:border-amber-500/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <ClipboardCheck className="text-amber-500" size={18} />
              <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
                {t("actor_profile.tile_title")}
              </div>
            </div>
            <p className="text-stone-400 text-sm mt-3">{t("actor_profile.tile_body")}</p>
          </motion.button>
        </div>
      ) : view === "actor_profile" ? (
        <div className="mt-8">
          <ActorProfile
            onBack={() => setView("root")}
            onCreatePath={{
              uk_agent: openUkAgentTemplate,
              get_more_auditions: openGetMoreAuditionsTemplate,
              more_castings_pl: openMoreCastingsPlTemplate,
              casting_directors_pl: () => openNewFromSuggestion("Zbudować bazę polskich reżyserów castingu"),
            }}
            onBlank={openBlank}
          />
        </div>
      ) : (
        <div className="mt-8 space-y-6">
          <div className="paper p-5 border-amber-500/30 bg-amber-500/5 text-amber-200 text-sm italic">
            {t("plan.top_note")}
          </div>

          <p className="text-stone-300 text-[15px] leading-relaxed max-w-3xl">
            {t("plan.intro")}
          </p>

          {/* Paid Career Path templates — visible for everyone. Fully
              unlocked for LEO START / MONTHLY / ANNUAL. FREE / SINGLE SCENE /
              48h SPRINT users see the tile with a lock and, on click, land on
              a shared preview page with the CTA + 3 upgrade plans. */}
          {(() => {
            const unlocked = ["start", "monthly", "annual", "light"].includes(access?.plan);
            const onOpenUk = () => {
              if (accessLoading) return;
              if (!unlocked) { nav("/career/preview/uk_agent"); return; }
              openUkAgentTemplate();
            };
            const onOpenGma = () => {
              if (accessLoading) return;
              if (!unlocked) { nav("/career/preview/get_more_auditions"); return; }
              openGetMoreAuditionsTemplate();
            };
            return (
              <>
                <motion.button
                  data-testid="path-template-uk-agent"
                  disabled={accessLoading}
                  aria-busy={accessLoading}
                  onClick={onOpenUk}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className="paper p-5 text-left hover:border-amber-500/50 transition-colors w-full disabled:opacity-60 disabled:cursor-wait"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
                      {t("path.uk_agent_eyebrow")}
                    </div>
                    {!unlocked && (
                      <span
                        data-testid="path-template-uk-agent-lock"
                        className="mono text-[10px] tracking-widest text-amber-500/80 uppercase"
                      >
                        🔒 {t("path.paid_lock_badge")}
                      </span>
                    )}
                  </div>
                  <div className="serif text-2xl text-stone-100 mt-1">
                    {!unlocked && <span aria-hidden className="mr-1">🔒</span>}
                    {t("path.uk_agent_title")}
                  </div>
                  <p className="text-stone-400 text-sm mt-2">
                    {t("path.uk_agent_body")}
                  </p>
                  {!unlocked && (
                    <p className="text-amber-500/80 text-xs mt-3">
                      {t("path.paid_locked_note")}
                    </p>
                  )}
                </motion.button>

                <motion.button
                  data-testid="path-template-get-more-auditions"
                  disabled={accessLoading}
                  aria-busy={accessLoading}
                  onClick={onOpenGma}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className="paper p-5 text-left hover:border-amber-500/50 transition-colors w-full disabled:opacity-60 disabled:cursor-wait"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
                      {t("path.gma_eyebrow")}
                    </div>
                    {!unlocked && (
                      <span
                        data-testid="path-template-get-more-auditions-lock"
                        className="mono text-[10px] tracking-widest text-amber-500/80 uppercase"
                      >
                        🔒 {t("path.paid_lock_badge")}
                      </span>
                    )}
                  </div>
                  <div className="serif text-2xl text-stone-100 mt-1">
                    {!unlocked && <span aria-hidden className="mr-1">🔒</span>}
                    {t("path.gma_title")}
                  </div>
                  <p className="text-stone-400 text-sm mt-2">
                    {t("path.gma_body")}
                  </p>
                  {!unlocked && (
                    <p className="text-amber-500/80 text-xs mt-3">
                      {t("path.paid_locked_note")}
                    </p>
                  )}
                </motion.button>

                {/* BĄDŹ WIDOCZNY / GET SEEN — paid, flexible-duration template */}
                {(() => {
                  const GS = GET_SEEN_TEMPLATE[lang === "pl" ? "pl" : "en"];
                  return (
                    <motion.button
                      data-testid="path-template-get-seen"
                      disabled={accessLoading}
                      aria-busy={accessLoading}
                      onClick={() => {
                        if (accessLoading) return;
                        if (!unlocked) {
                          if (access?.plan === "free") nav("/pricing"); else setCareerPaywallOpen(true);
                          return;
                        }
                        openGetSeenTemplate();
                      }}
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      className="paper p-5 text-left hover:border-amber-500/50 transition-colors w-full disabled:opacity-60 disabled:cursor-wait"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
                          {GS.eyebrow}
                        </div>
                        {!unlocked && (
                          <span data-testid="path-template-get-seen-lock" className="mono text-[10px] tracking-widest text-amber-500/80 uppercase">
                            🔒 {t("path.paid_lock_badge")}
                          </span>
                        )}
                      </div>
                      <div className="serif text-2xl text-stone-100 mt-1">
                        {!unlocked && <span aria-hidden className="mr-1">🔒</span>}
                        {GS.subtitle}
                      </div>
                      <p className="text-stone-400 text-sm mt-2">{GS.tileBody}</p>
                      {!unlocked && (
                        <p className="text-amber-500/80 text-xs mt-3">{t("path.paid_locked_note")}</p>
                      )}
                    </motion.button>
                  );
                })()}

                {/* PL-only paid Career Path — Zwiększ szanse na castingi w Polsce */}
                {lang === "pl" && (
                  <motion.button
                    data-testid="path-template-more-castings-pl"
                    disabled={accessLoading}
                    aria-busy={accessLoading}
                    onClick={() => {
                      if (accessLoading) return;
                      if (!unlocked) { nav("/career/preview/more_castings_pl"); return; }
                      openMoreCastingsPlTemplate();
                    }}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="paper p-5 text-left hover:border-amber-500/50 transition-colors w-full disabled:opacity-60 disabled:cursor-wait"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
                        Gotowa ścieżka
                      </div>
                      {!unlocked && (
                        <span
                          data-testid="path-template-more-castings-pl-lock"
                          className="mono text-[10px] tracking-widest text-amber-500/80 uppercase"
                        >
                          🔒 {t("path.paid_lock_badge")}
                        </span>
                      )}
                    </div>
                    <div className="serif text-2xl text-stone-100 mt-1">
                      {!unlocked && <span aria-hidden className="mr-1">🔒</span>}
                      Zwiększ liczbę castingów w Polsce
                    </div>
                    <p className="text-stone-400 text-sm mt-2">
                      10-krokowa ścieżka: aktualne materiały, biblioteka scen, reżyserzy obsady, mapa relacji, kontakt, self-tape i system dalszego działania.
                    </p>
                    {!unlocked && (
                      <p className="text-amber-500/80 text-xs mt-3">
                        {t("path.paid_locked_note")}
                      </p>
                    )}
                  </motion.button>
                )}
              </>
            );
          })()}

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="label-cap">{t("plan.suggestions_title")}</div>
            <button data-testid="plan-add-blank" onClick={openBlank} className="pill-btn">
              <Plus size={14} /> {t("plan.add_goal")}
            </button>
          </div>

          {/* MASTERMIND tile — reusable workflow, NOT a career_goal */}
          <button
            type="button"
            data-testid="career-mastermind-tile"
            onClick={() => nav("/career/mastermind-next")}
            className="paper p-5 text-left hover:border-amber-500/60 transition-colors block w-full"
          >
            <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
              MASTERMIND
            </div>
            <div className="serif text-2xl text-stone-100 mt-2">{t("mm.title")}</div>
            <div className="text-stone-300 italic text-sm mt-1">{t("mm.subtitle")}</div>
            <p className="text-stone-400 text-sm mt-3 whitespace-pre-line">
              {t("mm.description")}
            </p>
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(SUGGESTIONS[lang] || SUGGESTIONS.en || []).map((s) => (
              <button
                key={s}
                data-testid={`plan-suggestion-${s.slice(0,20).replace(/[^a-z0-9]/gi,'-')}`}
                onClick={() => openNewFromSuggestion(s)}
                className="paper p-3 text-left text-sm text-stone-200 hover:border-amber-500/50 hover:text-amber-300 transition-colors"
              >
                <ChevronRight size={12} className="inline mr-1 text-amber-500" />
                {s}
              </button>
            ))}
          </div>

          <div className="mt-8" ref={activeGoalsRef} data-testid="career-active-paths-section">
            <div className="label-cap mb-3">{t("path.title")}</div>
            {loading ? (
              <div className="mono text-stone-500 text-sm">{t("dash.loading")}</div>
            ) : goals.length === 0 ? (
              <div className="paper p-6 text-stone-500 text-sm text-center">
                {t("goal.no_goals")}
              </div>
            ) : (
              <ul className="space-y-3">
                {goals.map((g) => (
                  <li key={g.goal_id} className="paper p-4">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <Link
                        to={`/career/goals/${g.goal_id}`}
                        data-testid={`goal-open-${g.goal_id}`}
                        className="serif text-xl text-stone-100 hover:text-amber-400"
                      >
                        {g.title}
                      </Link>
                      <div className="flex items-center gap-2">
                        <span className="mono text-[11px] text-amber-500 uppercase tracking-widest">
                          {g.progress_pct}%
                        </span>
                        <button
                          onClick={() => delGoal(g)}
                          className="ghost-btn !py-1 !px-2 text-xs hover:!text-rose-400 hover:!border-rose-500/50"
                          data-testid={`goal-delete-${g.goal_id}`}
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 h-1.5 bg-stone-900 rounded overflow-hidden">
                      <div
                        className="h-full bg-amber-500 transition-all"
                        style={{ width: `${g.progress_pct}%` }}
                      />
                    </div>
                    {g.next_step && (
                      <div className="mt-3 text-sm">
                        <span className="mono text-[10px] text-stone-500 uppercase tracking-widest">
                          {t("step.next_hero")}:
                        </span>{" "}
                        <span className="text-stone-100">{g.next_step.name}</span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <GoalFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        prefillTitle={prefillTitle}
        template={formTemplate}
        onCreated={onGoalCreated}
      />

      <WhatNextPaywall
        open={careerPaywallOpen && access?.plan !== "free"}
        kind="career"
        lang={lang}
        onClose={() => setCareerPaywallOpen(false)}
        onSeeAllPlans={() => { setCareerPaywallOpen(false); nav("/pricing"); }}
      />
    </div>
  );
}
